import { Typography, Grid, Paper, Box, Button, Link, Alert, FormControlLabel, Switch, Divider } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm, FieldValues, Control } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import { Service } from "../../app/models/service";
import { useAppDispatch } from "../../app/store/configureStore";
import agent from "../../app/api/agent";
import { setService } from "./servicesSlice";
import AppCheckbox from "../../app/components/AppCheckbox";
import AutocompleteInputOption from "../../app/models/autocompleteInputOption";
import AppAutocomplete from "../../app/components/AppAutocomplete";
import AppDropzone from "../../app/components/AppDropzone";
import AppBreadcrumbs from "../../app/components/AppBreadcrumbs";
import AppReturn from "../../app/components/AppReturn";
import { getFileFormat } from "../../app/util/util";
import AppTextInput from "../../app/components/AppTextInput";
import { yupResolver } from "@hookform/resolvers/yup";
import { serviceValidationSchema } from "./serviceValidation";
import AppNumberInput from "../../app/components/AppNumberInput";
import { toast } from "react-toastify";
import { Question } from "../../app/models/question";
import AppQuestionEditor from "../../app/components/AppQuestionEditor";

interface Props {
    service?: Service;
    cancelEdit: () => void;
}

interface ServiceFormData {
    isActive: boolean;
    isGraphical: boolean;
    name: string;
    basePrice: number;
    category: string;
    subservices: string;
    file?: any;
    packageTestFile?: any;
    packageTestKeyFile?: any;
    questionsJson?: string;
}

export default function ServiceForm({ service, cancelEdit }: Props) {
    const dispatch = useAppDispatch();
    const { control, handleSubmit, setValue, formState: { isSubmitting, isDirty }, watch, reset } = useForm<ServiceFormData>({
        resolver: yupResolver(serviceValidationSchema) as any,
        defaultValues: {
            isActive: service?.isActive ?? true,
            // Jeśli edytujemy usługę, przyjmujemy, że gdy isGraphical === true, to test jest graficzny, czyli tekstowy jest false.
            isGraphical: service?.isGraphical ?? false,
            name: service?.name ?? "",
            basePrice: service?.basePrice ?? 0,
            category: service?.category ?? "",
            subservices: service?.subservices ?? "",
            file: service?.imagePath ?? "",
            packageTestFile: service?.packageTestPath ?? "",
            packageTestKeyFile: service?.packageTestKeyPath ?? ""
        }
    });

    // Lokalne zarządzanie trybem testu – stan oparty na Switchu
    // Jeśli test ma być tekstowy, isTextBasedTest === true, a wówczas service.isGraphical === false.
    const [isTextBasedTest, setIsTextBasedTest] = useState<boolean>(service ? !service.isGraphical : true);

    const handleTestFormatChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsTextBasedTest(event.target.checked);
    };

    // Obserwujemy pola plików
    const watchFile = watch('file');
    const watchTestFile = watch('packageTestFile');
    const watchTestKeyFile = watch('packageTestKeyFile');

    const [possibleSubservices, setPossibleSubservices] = useState<AutocompleteInputOption[]>([]);

    // Lokalny stan dla pytań testowych
    const [localQuestions, setLocalQuestions] = useState<Question[]>([]);

    // Inicjalizacja możliwych subusług
    useEffect(() => {
        agent.Subservices.fetchPossibleSubservices()
            .then(result => setPossibleSubservices(result))
            .catch(error => console.log(error));
    }, []);

    useEffect(() => {
        // Jeśli tworzymy nową usługę (service jest undefined)
        if (!service) {
            if (isTextBasedTest) {
                setLocalQuestions([
                    {
                        id: "1",
                        text: "",
                        options: ["", ""],
                        correctOptionIndex: 0,
                    },
                ]);
            } else {
                setLocalQuestions([]);
            }
            return;
        }

        // Jeśli edytujemy istniejącą usługę
        if (service.questionsJson && typeof service.questionsJson === "string" && service.questionsJson.trim() !== "") {
            try {
                const questions: Question[] = JSON.parse(service.questionsJson);
                if (questions.length > 0) {
                    setLocalQuestions(questions);
                    return;
                }
            } catch (error) {
                console.error("Błąd parsowania pytań:", error);
            }
        }
        // Jeśli usługa istnieje, ale nie ma pytań lub pole jest puste
        if (isTextBasedTest) {
            setLocalQuestions([
                {
                    id: "1",
                    text: "",
                    options: ["", ""],
                    correctOptionIndex: 0,
                },
            ]);
        } else {
            setLocalQuestions([]);
        }
    }, [service, isTextBasedTest]);


    useEffect(() => {
        if (service && !isDirty) reset(service);
    }, [service, reset, isDirty]);

    useEffect(() => {
        setValue("questionsJson", JSON.stringify(localQuestions), { shouldDirty: true });
    }, [localQuestions, setValue]);

    async function handleSubmitData(data: ServiceFormData) {
        try {
            // Ustawiamy isGraphical jako zaprzeczenie trybu tekstowego
            data.isGraphical = !isTextBasedTest;
            if (isTextBasedTest) {
                // Transformacja pytań:
                const validQuestions = localQuestions
                    .map((q) => ({
                        ...q,
                        text: q.text.trim(),
                        options: q.options.map((option) => option.trim()).filter((option) => option !== "")
                    }))
                    .filter((q) => q.text !== "" && q.options.length >= 2);

                // Jeśli nie ma żadnych poprawnych pytań, wyświetlamy błąd i przerywamy zapis
                if (validQuestions.length === 0) {
                    toast.error("Musisz podać przynajmniej jedno pytanie z minimum dwiema odpowiedziami, a żadne pole nie może być puste.");
                    return;
                }

                // Nadajemy sekwencyjne id (1, 2, 3, ...)
                const sequentialQuestions = validQuestions.map((q, index) => ({
                    ...q,
                    id: (index + 1).toString()
                }));
                data.questionsJson = JSON.stringify(sequentialQuestions);

                // W trybie tekstowym pola dla plików ustawiamy na undefined
                data.packageTestFile = undefined;
                data.packageTestKeyFile = undefined;
            }
            let response: Service;
            if (service) {
                response = await agent.Services.update(data);
                toast.success("Uaktualniono usługę");
            } else {
                response = await agent.Services.create(data);
                toast.success("Dodano usługę");
            }
            dispatch(setService(response));
            cancelEdit();
        } catch (error) {
            console.log(error);
        }
    }

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn title={"Tworzenie/edycja usługi"} cancelEdit={cancelEdit} />
            <Box component={Paper} sx={{ p: 4 }}>
                <form onSubmit={handleSubmit(handleSubmitData)}>
                    <Grid container spacing={3}>
                        {/* Aktywność usługi */}
                        <Grid item xs={12} sm={4}>
                            <AppCheckbox
                                control={control as unknown as Control<FieldValues>}
                                name='isActive'
                                label='Widoczne i aktywne'
                                defaultChecked
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <AppTextInput control={control as unknown as Control<FieldValues>} name='name' label='Nazwa' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AppNumberInput control={control as unknown as Control<FieldValues>} name='basePrice' label='Cena bazowa' />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <AppTextInput control={control as unknown as Control<FieldValues>} name='category' label='Kategoria' />
                        </Grid>
                        {service?.imagePath && (
                            <Grid item xs={12} sm={12}>
                                <img
                                    src={service.imagePath}
                                    alt="Podgląd istniejącego"
                                    style={{ maxHeight: 200, marginTop: 35 }}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={12}>
                            <AppAutocomplete
                                control={control as unknown as Control<FieldValues>}
                                name='subservices'
                                label='Podusługi (moduły)'
                                items={possibleSubservices}
                                multiple
                                selectedids={service?.subservices}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end">
                                <Typography variant='h4' sx={{ mt: 1, mb: 1 }}>Zdjęcie usługi:</Typography>
                                <AppDropzone
                                    name="file"
                                    control={control as unknown as Control<FieldValues>}
                                    acceptedFormats="jpg,jpeg,png"
                                />
                                {watchFile ? (
                                    <img src={watchFile.preview} alt="Plik" style={{ maxHeight: 200, marginTop: 35 }} />
                                ) : (
                                    service?.imagePath && (
                                        <img
                                            src={service.imagePath}
                                            alt="existing preview"
                                            style={{ maxHeight: 200, marginTop: 35 }}
                                        />
                                    )
                                )}
                            </Box>
                        </Grid>
                        {/* Test format selector */}
                        <Grid item xs={12} sm={12}>
                            <Divider sx={{ my: 3 }} />
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Usługa może posiadać równolegle dodany test tekstowy i graficzny, ale od poniższego "suwaka" zależy, która opcja będzie używana.
                            </Alert>
                            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isTextBasedTest}
                                            onChange={handleTestFormatChange}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="h6">
                                            {isTextBasedTest ? "Test tekstowy" : "Test graficzny (PDF/JPG/PNG)"}
                                        </Typography>
                                    }
                                />
                            </Box>
                            <Typography variant="body1" align="center" color="textSecondary">
                                {isTextBasedTest
                                    ? "Utwórz pytania i odpowiedzi w formie tekstowej"
                                    : "Wgraj arkusz testu i klucz odpowiedzi jako pliki PDF/JPG/PNG"}
                            </Typography>
                        </Grid>
                        {/* Renderowanie sekcji dla testu graficznego */}
                        {!isTextBasedTest && (
                            <>
                                {/* Dla wzoru arkusza egzaminu */}
                                <Grid item xs={12} sm={12} marginTop={3}>
                                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end">
                                        <Typography variant='h4' sx={{ mt: 1, mb: 1 }} textAlign='center'>
                                            Wzór arkusza egzaminu:
                                        </Typography>
                                        <AppDropzone
                                            name="packageTestFile"
                                            control={control as unknown as Control<FieldValues>}
                                            acceptedFormats="pdf,jpg,png"
                                        />
                                        {watchTestFile ? (
                                            typeof watchTestFile === 'string' ? (
                                                // Dla URL-i (ciągów znaków)
                                                getFileFormat(watchTestFile) === "pdf" ? (
                                                    <Link sx={{ mt: 2 }} href={watchTestFile} target="_blank">
                                                        {watchTestFile}
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={watchTestFile}
                                                        alt="Podgląd arkusza"
                                                        style={{ maxHeight: 200, marginTop: 35 }}
                                                    />
                                                )
                                            ) : (
                                                // Dla obiektów File
                                                <Alert severity="info" sx={{ mt: 2 }}>
                                                    Podgląd plików pdf dostępny jest po zapisaniu usługi
                                                </Alert>
                                            )
                                        ) : (
                                            service?.packageTestPath && (
                                                getFileFormat(service.packageTestPath) === "pdf" ? (
                                                    <Link sx={{ mt: 2 }} href={service.packageTestPath} target="_blank">
                                                        {service.packageTestPath}
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={service.packageTestPath}
                                                        alt="Podgląd arkusza"
                                                        style={{ maxHeight: 200, marginTop: 35 }}
                                                    />
                                                )
                                            )
                                        )}
                                    </Box>
                                </Grid>

                                {/* Klucz odpowiedzi */}
                                <Grid item xs={12} sm={12} marginTop={3}>
                                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="flex-end">
                                        <Typography variant='h4' sx={{ mt: 1, mb: 1 }}>
                                            Klucz odpowiedzi:
                                        </Typography>
                                        <AppDropzone
                                            name="packageTestKeyFile"
                                            control={control as unknown as Control<FieldValues>}
                                            acceptedFormats="pdf,png,jpg"
                                        />
                                        {watchTestKeyFile ? (
                                            typeof watchTestKeyFile === 'string' ? (
                                                // Dla URL-i (ciągów znaków)
                                                getFileFormat(watchTestKeyFile) === "pdf" ? (
                                                    <Link sx={{ mt: 2 }} href={watchTestKeyFile} target="_blank">
                                                        {watchTestKeyFile}
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={watchTestKeyFile}
                                                        alt="Podgląd klucza odpowiedzi"
                                                        style={{ maxHeight: 200, marginTop: 35 }}
                                                    />
                                                )
                                            ) : (
                                                // Dla obiektów File
                                                <Alert severity="info" sx={{ mt: 2 }}>
                                                    Podgląd plików pdf dostępny jest po zapisaniu usługi
                                                </Alert>
                                            )
                                        ) : (
                                            service?.packageTestKeyPath && (
                                                getFileFormat(service.packageTestKeyPath) === "pdf" ? (
                                                    <Link sx={{ mt: 2 }} href={service.packageTestKeyPath} target="_blank">
                                                        {service.packageTestKeyPath}
                                                    </Link>
                                                ) : (
                                                    <img
                                                        src={service.packageTestKeyPath}
                                                        alt="Podgląd klucza odpowiedzi"
                                                        style={{ maxHeight: 200, marginTop: 35 }}
                                                    />
                                                )
                                            )
                                        )}
                                    </Box>
                                </Grid>
                            </>
                        )}

                        {/* Renderowanie sekcji dla testu tekstowego */}
                        {isTextBasedTest && (
                            <Grid item xs={12}>
                                <AppQuestionEditor
                                    questions={localQuestions}
                                    onQuestionsChange={setLocalQuestions}
                                />
                            </Grid>
                        )}
                    </Grid>
                    <Box display='flex' justifyContent='space-between' sx={{ mt: 3 }}>
                        <Button onClick={cancelEdit} variant='outlined' color='inherit'>Anuluj</Button>
                        <LoadingButton disabled={!isDirty} loading={isSubmitting} type='submit' variant='outlined' color='success'>
                            Zapisz
                        </LoadingButton>
                    </Box>
                </form>
            </Box>
        </>
    );
}